import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useDispatch, useSelector } from 'react-redux';
import { handleSizeChange, sizes, updateMeasurementPositions, } from '../../Editor/DecalEditorCanvasSizing';
import { UPDATE_CANVAS_SIZE } from '../../Rutab/RutabConstants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChangeForm, } from '../../Editor/CanvasFormService';
import React from 'react';
import { translate } from "../../Services/translation";
import { saveCanvasState } from '../../Actions/Rutab/HistoryActions';
export const SizeMenuContent = React.memo(({ canvas, canvasRef }) => {
    const canvasSize = useSelector((state) => state.decalEditor.canvasSize, (prev, next) => prev.width === next.width &&
        prev.height === next.height &&
        prev.sizeKey === next.sizeKey &&
        prev.orientation === next.orientation);
    const selectedForm = useSelector((state) => state.decalEditor.selectedForm);
    const selectedHoles = useSelector((state) => state.decalEditor.holePlacement);
    const canvasType = useSelector((state) => state.decalEditor.canvasType);
    const isUndoRedoInProgress = useSelector((state) => state.decalEditor.isUndoRedoInProgress);
    const dispatch = useDispatch();
    const [width, setWidth] = useState(canvasSize.width);
    const [height, setHeight] = useState(canvasSize.height);
    const [orientation, setOrientation] = useState(canvasSize.orientation);
    const initialRenderComplete = useRef(false);
    useEffect(() => {
        if (!initialRenderComplete.current) {
            initialRenderComplete.current = true;
            return;
        }
        const mmWidth = canvasSize.width;
        const mmHeight = canvasSize.height;
        handleSizeChange(canvasSize, canvas, selectedHoles);
        updateMeasurementPositions(mmHeight, mmWidth, canvasRef);
        setOrientation(canvasSize.orientation);
        setHeight(mmHeight);
        setWidth(mmWidth);
        ChangeForm(canvas, canvasRef, selectedForm);
        if (!isUndoRedoInProgress) {
            dispatch(saveCanvasState(canvas));
        }
    }, [canvasSize]);
    const sizeChanged = (size) => {
        dispatch({
            type: UPDATE_CANVAS_SIZE,
            payload: {
                canvasSize: {
                    sizeKey: size,
                    width: sizes[size].width,
                    height: sizes[size].height,
                    orientation: canvasSize.orientation,
                },
            },
        });
    };
    const applyWidthChange = useCallback(() => {
        const newWidth = Math.max(canvasType.minHeight, Math.min(canvasType.maxWidth, width));
        if (newWidth !== width) {
            setWidth(newWidth);
        }
        dispatch({
            type: UPDATE_CANVAS_SIZE,
            payload: {
                canvasSize: {
                    sizeKey: 'Custom',
                    width: newWidth,
                    height: canvasSize.height,
                    orientation: canvasSize.orientation,
                },
            },
        });
    }, [
        height,
        width,
        canvas,
        canvasRef,
        dispatch,
        canvasSize.orientation,
    ]);
    const applyHeightChange = useCallback(() => {
        const newHeight = Math.max(canvasType.minHeight, Math.min(canvasType.maxHeight, height));
        if (newHeight !== height) {
            setHeight(newHeight);
        }
        dispatch({
            type: UPDATE_CANVAS_SIZE,
            payload: {
                canvasSize: {
                    sizeKey: 'Custom',
                    width: width,
                    height: newHeight,
                    orientation: canvasSize.orientation,
                },
            },
        });
    }, [
        height,
        width,
        canvas,
        canvasRef,
        dispatch,
        canvasSize.orientation,
    ]);
    const changeOrientation = useCallback((newOrientation) => {
        const needsSwap = newOrientation !== canvasSize.orientation;
        if (!needsSwap)
            return;
        const currentWidth = canvasSize.width;
        const currentHeight = canvasSize.height;
        let newWidth = currentHeight;
        let newHeight = currentWidth;
        const validatedHeight = Math.max(canvasType.minHeight, Math.min(canvasType.maxHeight, newHeight));
        const validatedWidth = Math.max(canvasType.minWidth, Math.min(canvasType.maxWidth, newWidth));
        dispatch({
            type: UPDATE_CANVAS_SIZE,
            payload: {
                canvasSize: {
                    sizeKey: canvasSize.sizeKey,
                    width: validatedWidth,
                    height: validatedHeight,
                    orientation: newOrientation,
                },
            },
        });
    }, [canvasSize, canvas, canvasRef, dispatch]);
    const sizeOptions = useMemo(() => (_jsxs(_Fragment, { children: [_jsx("option", { value: "A1", children: "A1 (841mm x 594mm)" }), _jsx("option", { value: "A2", children: "A2 (594mm x 420mm)" }), _jsx("option", { value: "A3", children: "A3 (420mm x 297mm)" }), _jsx("option", { value: "A4", children: "A4 (297mm x 210mm)" }), _jsx("option", { value: "A5", children: "A5 (210mm x 148mm)" }), canvasSize.sizeKey === 'Custom' ? (_jsx("option", { value: "Custom", children: translate('decal.sizemenu.sizeselector.custom.text') })) : null] })), [canvasSize.sizeKey, width, height]);
    return (_jsxs("div", { className: "size-selector-content-guide", children: [_jsxs("label", { className: "mb-1", children: [translate('decal.sizemenu.selectsize.header'), _jsx("select", { onChange: (e) => sizeChanged(e.target.value), value: canvasSize.sizeKey, style: {
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '16px',
                            margin: '8px 0',
                            width: '100%',
                            boxSizing: 'border-box',
                        }, children: sizeOptions })] }), _jsx("span", { children: translate('decal.sizemenu.orientation.header') }), _jsx("div", { className: "toggle-container mb-1", children: _jsxs("div", { className: "toggle-switch", children: [_jsx("button", { className: `toggle-btn ${orientation === 'landscape' ? 'active' : ''}`, onClick: () => changeOrientation('landscape'), children: translate('decal.sizemenu.button.landscape') }), _jsx("button", { className: `toggle-btn ${orientation === 'portrait' ? 'active' : ''}`, onClick: () => changeOrientation('portrait'), children: translate('decal.sizemenu.button.portrait') })] }) }), _jsx("div", { className: "size-inputs-container", children: _jsxs("div", { className: "size-inputs-row", children: [_jsxs("label", { children: [translate('decal.sizemenu.width.header'), _jsx("input", { type: "number", placeholder: "Width (mm)", min: canvasType.minWidth, max: canvasType.maxWidth, value: width, onChange: (e) => setWidth(parseInt(e.target.value, 10)), onBlur: applyWidthChange })] }), _jsxs("label", { children: [translate('decal.sizemenu.height.header'), _jsx("input", { type: "number", placeholder: "Height (mm)", min: canvasType.minHeight, max: canvasType.maxHeight, value: height, onChange: (e) => setHeight(parseInt(e.target.value, 10)), onBlur: applyHeightChange })] })] }) })] }));
});
